import { ActiveFilter } from 'modules/locals/components/LocalSearchFilters/guidesFilterData';
import { filterIsActive } from 'modules/locals/utils/LocalSearchFilterHelper';
import slugify from 'utils/slugify';

type CompileFunctionParams = {
  country?: string;
  city?: string;
  hostType?: string;
  activeFilters?: ActiveFilter[];
  page?: number;
  otherQueryParams?: URLSearchParams;
};
type URLParts = { pathParts: string[]; queryParts: URLSearchParams };

const getURLParts = (activeFilters?: ActiveFilter[]): URLParts => {
  const result: URLParts = {
    pathParts: [],
    queryParts: new URLSearchParams(),
  };

  if (!activeFilters || activeFilters.length === 0) {
    return result;
  }

  const otherFilter = activeFilters.find(
    (activeFilter) => activeFilter.uid === 'other',
  );
  const otherFilterIsActive = filterIsActive(otherFilter);
  const familyFriendlyFilterIsActive = otherFilterIsActive
    ? otherFilter.options.some((f) => f.value === 'family-friendly')
    : false;
  if (familyFriendlyFilterIsActive) {
    result.pathParts.push('family-friendly');
  }

  const languageFilter = activeFilters.find(
    (activeFilter) => activeFilter.uid === 'languages',
  );
  const languageFilterIsActive = filterIsActive(languageFilter);
  if (languageFilterIsActive) {
    // if there is no family friendly filter active, add first language in path, rest in search param
    // otherwise, add all languages to search params
    if (!familyFriendlyFilterIsActive) {
      result.pathParts.push(languageFilter.options[0].value);
      languageFilter.options
        .slice(1)
        .forEach((activeFilter) =>
          result.queryParts.append(languageFilter.uid, activeFilter.value),
        );
    } else {
      languageFilter.options.forEach((activeFilter) =>
        result.queryParts.append(languageFilter.uid, activeFilter.value),
      );
    }
  }

  const dateFromFilter = activeFilters.find(
    (activeFilter) => activeFilter.uid === 'date.from',
  );
  const dateFromFilterIsActive = filterIsActive(dateFromFilter);
  if (dateFromFilterIsActive) {
    dateFromFilter.options.forEach((activeFilter) =>
      result.queryParts.append(dateFromFilter.uid, activeFilter.value),
    );
  }

  const dateToFilter = activeFilters.find(
    (activeFilter) => activeFilter.uid === 'date.to',
  );
  const dateToFilterIsActive = filterIsActive(dateToFilter);
  if (dateToFilterIsActive) {
    dateToFilter.options.forEach((activeFilter) =>
      result.queryParts.append(dateToFilter.uid, activeFilter.value),
    );
  }

  return result;
};

/**
 * Builds a path from the local search filters
 */
const compile = ({
  country,
  city,
  hostType,
  activeFilters,
  page,
  otherQueryParams,
}: CompileFunctionParams): string => {
  const { pathParts: pathPartsFromFilters, queryParts } =
    getURLParts(activeFilters);

  if (otherQueryParams) {
    otherQueryParams.forEach((v, k) => queryParts.append(k, v));
  }

  const slugifiedCountry = slugify(country || '', false);
  const slugifiedCity = slugify(city || '', false);

  // we always require either a city or a country
  if (slugifiedCity === '' && slugifiedCountry === '') {
    return '/';
  }

  const pathParts = [
    slugifiedCountry,
    slugifiedCity,
    hostType || 'guides',
    ...pathPartsFromFilters,
  ].filter((val) => !!val);
  const path = pathParts.join('/') + (pathParts.length > 0 ? '/' : '');
  const queryString = queryParts.toString();
  const query =
    queryString.length > 0 ? `?${decodeURIComponent(queryString)}` : '';
  const result = `/${path}${query}`;

  return result;
};

export default {
  compile,
};
